/**
 * APIKeysPage
 * ===========
 * Users generate and manage API keys for connecting Claude Code
 * to the Xpert MCP server.
 *
 * Key UX rules:
 *  - Raw key is shown ONCE after creation, then never again
 *  - Copy-to-clipboard on both the key and the claude mcp add command
 *  - Max 10 active keys enforced by backend
 *  - Revoke requires confirmation (destructive and irreversible)
 *
 * Route: /api-keys  (ProtectedRoute)
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Key,
  Plus,
  Copy,
  Check,
  Trash2,
  AlertTriangle,
  Loader2,
  Terminal,
  Eye,
  EyeOff,
  ArrowLeft,
  Sparkles,
  Clock,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// ---------------------------------------------------------------------------
// Copy button with a momentary check icon
// ---------------------------------------------------------------------------

function CopyButton({ value, label = "Copy", className = "" }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1.5 text-sm transition-colors ${
        copied ? "text-brand-green" : "text-slateline-500 hover:text-ink"
      } ${className}`}
    >
      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      {copied ? "Copied!" : label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// One-time new key reveal panel
// ---------------------------------------------------------------------------

function NewKeyReveal({ keyData, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const mcpCommand = `claude mcp add xpert https://api.xpert.company/mcp/message --header "Authorization: Bearer ${keyData.raw_key}"`;

  return (
    <motion.div
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8 border border-emerald-500/40 bg-brand-green/5 rounded-sm overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-emerald-500/20 bg-brand-green/10">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-brand-green" />
          <span className="font-semibold text-emerald-300">
            API key created — save it now
          </span>
        </div>
        <span className="text-xs text-brand-green bg-brand-green/20 px-2 py-0.5 rounded">
          shown once
        </span>
      </div>

      <div className="p-5 space-y-5">
        {/* Key */}
        <div>
          <p className="text-xs text-slateline-400 mb-2 flex items-center gap-2">
            <Key className="w-3.5 h-3.5" />
            API Key ({keyData.key_prefix}…)
          </p>
          <div className="flex items-center gap-3 bg-canvas border border-slateline-200 rounded-sm px-4 py-3 font-mono text-sm">
            <span className="flex-1 text-emerald-300 truncate">
              {visible ? keyData.raw_key : "•".repeat(40)}
            </span>
            <button
              onClick={() => setVisible(!visible)}
              className="text-slateline-400 hover:text-ink transition-colors flex-shrink-0"
            >
              {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            <CopyButton value={keyData.raw_key} />
          </div>
        </div>

        {/* Claude Code setup command */}
        <div>
          <p className="text-xs text-slateline-400 mb-2 flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5" />
            Connect Claude Code (paste in your terminal)
          </p>
          <div className="flex items-start gap-3 bg-canvas border border-slateline-200 rounded-sm px-4 py-3">
            <code className="flex-1 text-xs text-slateline-600 break-all leading-relaxed">
              {mcpCommand}
            </code>
            <CopyButton value={mcpCommand} className="flex-shrink-0 mt-0.5" />
          </div>
        </div>

        {/* Dismiss */}
        <div className="flex items-center justify-between pt-1">
          <p className="text-xs text-slateline-400">
            This key will not be shown again. Store it securely.
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={onDismiss}
            className="border-slateline-200 text-slateline-500"
          >
            I've saved it
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Key row
// ---------------------------------------------------------------------------

function KeyRow({ apiKey, onRevoke }) {
  const isActive = apiKey.active;
  const lastUsed = apiKey.last_used_at
    ? new Date(apiKey.last_used_at).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      })
    : "Never";

  return (
    <div
      className={`flex items-center gap-4 px-5 py-4 border-b border-slateline-200 last:border-0 ${
        !isActive ? "opacity-50" : ""
      }`}
    >
      {/* Icon */}
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${
        isActive ? "bg-brand-green/10" : "bg-slateline-100"
      }`}>
        <Key className={`w-4 h-4 ${isActive ? "text-brand-green" : "text-slateline-300"}`} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="font-medium text-ink text-sm truncate">{apiKey.label}</p>
          {!isActive && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-slateline-100 text-slateline-400">
              revoked
            </span>
          )}
        </div>
        <p className="text-xs text-slateline-400 font-mono">{apiKey.key_prefix}…</p>
      </div>

      {/* Last used */}
      <div className="hidden md:flex items-center gap-1.5 text-xs text-slateline-400 flex-shrink-0">
        <Clock className="w-3.5 h-3.5" />
        {isActive ? `Last used: ${lastUsed}` : `Revoked`}
      </div>

      {/* Revoke */}
      {isActive && (
        <button
          onClick={() => onRevoke(apiKey)}
          className="p-2 text-slateline-300 hover:text-red-400 transition-colors flex-shrink-0"
          title="Revoke key"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function APIKeysPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyData, setNewKeyData] = useState(null);   // raw key shown once
  const [creating, setCreating] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [label, setLabel] = useState("Claude Code");
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/user/api-keys`);
      setKeys(res.data.keys || []);
    } catch (err) {
      toast.error("Failed to load API keys.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!label.trim()) { toast.error("Please enter a label."); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/user/api-keys`, { label: label.trim() });
      setNewKeyData(res.data);
      setCreateModal(false);
      setLabel("Claude Code");
      await fetchKeys();     // refresh list (new key appears, masked)
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create key.");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await axios.delete(`${API}/user/api-keys/${revokeTarget.id}`);
      toast.success(`Key "${revokeTarget.label}" revoked.`);
      setRevokeTarget(null);
      await fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke key.");
    } finally {
      setRevoking(false);
    }
  };

  const activeKeys   = keys.filter(k => k.active);
  const revokedKeys  = keys.filter(k => !k.active);
  const atLimit      = activeKeys.length >= 10;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-canvas">
      <MarketingNav />

      <div className="max-w-2xl mx-auto px-4 pt-28 pb-16">

        {/* Back */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-slateline-500 hover:text-ink transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-brand-green/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-ink">Claude MCP Access</h1>
              <p className="text-slateline-500 text-sm">
                Connect Claude to Xpert and book experts directly from any conversation
              </p>
            </div>
          </div>

          {/* How it works strip */}
          <div className="flex items-center gap-2 overflow-x-auto py-3 text-xs text-slateline-400 whitespace-nowrap">
            <span className="bg-brand-green/10 text-brand-green px-2 py-1 rounded">1. Generate key</span>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
            <span className="bg-slateline-100 px-2 py-1 rounded">2. Run <code className="text-slateline-600">claude mcp add</code></span>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
            <span className="bg-slateline-100 px-2 py-1 rounded">3. Ask Claude to find an expert</span>
            <ChevronRight className="w-3 h-3 flex-shrink-0" />
            <span className="bg-slateline-100 px-2 py-1 rounded">4. Claude books, you pay, expert prepares</span>
          </div>
        </motion.div>

        {/* New key reveal (shown once) */}
        <AnimatePresence>
          {newKeyData && (
            <NewKeyReveal
              keyData={newKeyData}
              onDismiss={() => setNewKeyData(null)}
            />
          )}
        </AnimatePresence>

        {/* Keys card */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-canvas-card border border-slateline-200 rounded-sm overflow-hidden"
        >
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-slateline-200">
            <div>
              <p className="font-semibold text-ink">API Keys</p>
              <p className="text-xs text-slateline-400 mt-0.5">
                {activeKeys.length} of 10 active
              </p>
            </div>
            <Button
              onClick={() => setCreateModal(true)}
              disabled={atLimit}
              className="bg-brand-green hover:bg-brand-greenHover text-white text-sm font-semibold"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              New Key
            </Button>
          </div>

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-brand-green animate-spin" />
            </div>
          ) : keys.length === 0 ? (
            <div className="text-center py-14 px-6">
              <Key className="w-10 h-10 text-slateline-300 mx-auto mb-3" />
              <p className="text-slateline-500 mb-1">No API keys yet</p>
              <p className="text-slateline-300 text-sm mb-5">
                Create one to connect Claude Code to Xpert
              </p>
              <Button
                onClick={() => setCreateModal(true)}
                className="bg-brand-green hover:bg-brand-greenHover text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Key
              </Button>
            </div>
          ) : (
            <div>
              {activeKeys.map(k => (
                <KeyRow key={k.id} apiKey={k} onRevoke={setRevokeTarget} />
              ))}
              {revokedKeys.length > 0 && (
                <>
                  <div className="px-5 py-2 bg-canvas/50 border-t border-slateline-200">
                    <p className="text-xs text-slateline-300 uppercase tracking-widest">Revoked</p>
                  </div>
                  {revokedKeys.map(k => (
                    <KeyRow key={k.id} apiKey={k} onRevoke={setRevokeTarget} />
                  ))}
                </>
              )}
            </div>
          )}

          {/* Limit warning */}
          {atLimit && (
            <div className="px-5 py-3 border-t border-slateline-200 flex items-center gap-2 bg-amber-500/5">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
              <p className="text-xs text-amber-400">
                10-key limit reached. Revoke an existing key to create a new one.
              </p>
            </div>
          )}
        </motion.div>

        {/* Security notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-5 p-4 bg-canvas-card border border-slateline-200 rounded-sm"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-brand-green flex-shrink-0 mt-0.5" />
            <div className="text-xs text-slateline-400 space-y-1">
              <p className="text-slateline-500 font-medium">Security</p>
              <p>Raw keys are stored as SHA-256 hashes — not even Xpert can recover them.</p>
              <p>Each key grants access only to your account. Revoke immediately if compromised.</p>
              <p>Keys are scoped: they can search experts, submit written reviews, and check their status — but cannot access your payment methods or account credentials.</p>
            </div>
          </div>
        </motion.div>

      </div>

      {/* ------------------------------------------------------------------- */}
      {/* Create modal                                                          */}
      {/* ------------------------------------------------------------------- */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="bg-canvas-card border-slateline-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-ink flex items-center gap-2">
              <Key className="w-5 h-5 text-brand-green" />
              New API Key
            </DialogTitle>
            <DialogDescription className="text-slateline-500">
              Give this key a label so you can identify it later.
              The raw key is shown once after creation.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <label className="text-sm text-slateline-600 block mb-2">Label</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !creating && handleCreate()}
              placeholder="e.g. Claude Code — MacBook"
              maxLength={100}
              className="w-full bg-canvas border border-slateline-200 rounded-sm px-3 py-2.5 text-ink text-sm placeholder-slate-600 focus:outline-none focus:border-brand-green"
            />
            <p className="text-xs text-slateline-300 mt-1">{label.length}/100</p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setCreateModal(false); setLabel("Claude Code"); }}
              className="border-slateline-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !label.trim()}
              className="bg-brand-green hover:bg-brand-greenHover text-white font-semibold"
            >
              {creating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating…</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Create Key</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------------------------- */}
      {/* Revoke confirm modal                                                  */}
      {/* ------------------------------------------------------------------- */}
      <Dialog open={!!revokeTarget} onOpenChange={open => !open && setRevokeTarget(null)}>
        <DialogContent className="bg-canvas-card border-slateline-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-ink flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Revoke API Key?
            </DialogTitle>
            <DialogDescription className="text-slateline-500">
              <span className="text-ink font-medium">"{revokeTarget?.label}"</span> (
              {revokeTarget?.key_prefix}…) will stop working immediately.
              Any Claude Code session using this key will lose access.
              <span className="block mt-2 font-medium text-red-400">This cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setRevokeTarget(null)}
              className="border-slateline-200"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRevoke}
              disabled={revoking}
              className="bg-red-600 hover:bg-red-500 text-ink"
            >
              {revoking ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Revoking…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" />Revoke Key</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
